import * as THREE from 'three';
import { GEAR_BASES, type GearBaseId, type Rarity } from '@somnhold/content';
import { DSTEEL, DWOOD, LEATHER_BELT, STEEL, STRING, WOOD, cloth, emis, leather, metal, std, stone } from '../materials.ts';
import { G, M, V, glowSprite, heaterShield, limb, ring } from '../prims.ts';
import { pauldron } from './hero-tiers.ts';
import { begin, done, type BaseOpts, type Model } from './types.ts';

/* ---------- the 44 gear bases (docs/EQUIPO.md §2), for their cards ---------- */

export type GearRarity = Rarity;
export const GEAR_RARITIES: readonly GearRarity[] = ['common', 'uncommon', 'rare', 'veryRare', 'unique'];

/** Rune color per rarity (common carries none). */
const RUNE: Record<GearRarity, string> = { common: '#c4c8d2', uncommon: '#47d16a', rare: '#3a8bff', veryRare: '#b05cff', unique: '#ff4f3a' };
const RUNE_LIGHT: Record<GearRarity, string> = { common: '#ffffff', uncommon: '#a4f0b6', rare: '#a8ccff', veryRare: '#dcb4ff', unique: '#ff8a6e' };
/** How many runes and how big a gem each rarity shows. */
const RUNES: Record<GearRarity, number> = { common: 0, uncommon: 2, rare: 3, veryRare: 4, unique: 5 };
const GEM: Record<GearRarity, number> = { common: 0, uncommon: 0, rare: 0.8, veryRare: 1, unique: 1.2 };

/**
 * The card camera (renderThumb) looks at a model turned -0.6 from a direction 0.67 rad off +Z: a piece built
 * facing +Z would be seen edge-on. Every piece turns this much so its face (+Z) looks at the card, a little to
 * the side.
 */
const FACE = 1.05;

/** Materials by weight and a few shared paints (the dressed hero wears the same ones, models/dress.ts). */
export const CLOTH_A = cloth('#3d4558');
export const CLOTH_B = cloth('#5a4a5e');
export const CLOTH_TRIM = cloth('#7a2e2a');
export const HIDE = leather('#5a3a24');
export const HIDE_D = leather('#3e281a');
export const MAIL = metal('#8a9098', { roughness: 0.55 });
export const PLATE = metal('#c2c8d0', { roughness: 0.22 });
const BLADE = metal('#b9c0c8', { roughness: 0.2 });
const HAFT = std('#4a3222', { roughness: 0.85 });
const SOLE = std('#1e1814', { roughness: 0.9 });
const BONE_M = std('#d8ccb0', { roughness: 0.7 });
/** The moss hood's living moss and its pale lichen, the absent's cloak's watch grey (docs/SABERES.md §4.4). */
export const MOSS = std('#4f6b36', { roughness: 1, flatShading: true });
export const MOSS_DK = std('#38502a', { roughness: 1, flatShading: true });
const LICHEN = std('#b9c7a8', { roughness: 0.9, flatShading: true });
const WATCH = cloth('#6b7078', { side: THREE.DoubleSide });
export const WATCH_DK = cloth('#41454c');
export const VIGIL_EYE = emis('#f2f6ff', '#bcd0ff', 1.4);
/** The mason's hammer's dressed-stone face (docs/SABERES.md §4.4). */
const MASON_FACE = std('#9a9ea6', { roughness: 0.92, flatShading: true });

/** A gear piece in its own frame: what to decorate and where. */
interface Piece {
  /** Runes go along this segment (in `at`'s frame), facing +Z. */
  rune?: [THREE.Vector3, THREE.Vector3];
  /** Where the rarity gem sits (in `at`'s frame). */
  gem?: THREE.Vector3;
  at: THREE.Object3D;
  /** Or along an arc in the XY plane (rings): radius, depth of the face, angles from +X. */
  arc?: { r: number; z: number; a0: number; a1: number };
  /** Or round a horizontal band (head pieces): radius, height, angles from +Z toward +X. */
  band?: { r: number; y: number; a0: number; a1: number };
  /** Rune size multiplier (small pieces want smaller glyphs). */
  rs?: number;
}

/** A shaft with a grip wrap and a pommel cap, from y0 to y1. */
const haft = (p: THREE.Object3D, y0: number, y1: number, r: number, mat: THREE.Material = HAFT): void => {
  limb(p, V(0, y0, 0), V(0, y1, 0), r, r, mat, 6);
  M(new THREE.CylinderGeometry(r * 1.25, r * 1.25, Math.min(0.2, (y1 - y0) * 0.22), 6), LEATHER_BELT, p).position.y = y0 + 0.13;
  M(new THREE.SphereGeometry(r * 1.5, 6, 4), DSTEEL, p).position.y = y0;
};

/**
 * A sword along +Y from the pommel at the origin: grip of `grip`, a cross of `guard` at y = grip, then a blade
 * of `len` and width `w` tapering to its point, a darker fuller down the middle.
 */
const longBlade = (p: THREE.Object3D, len: number, w: number, grip: number, guard: number): void => {
  M(new THREE.SphereGeometry(w * 0.38, 6, 4), DSTEEL, p);
  M(new THREE.CylinderGeometry(w * 0.22, w * 0.22, grip, 6), LEATHER_BELT, p).position.y = grip / 2;
  M(new THREE.BoxGeometry(guard, w * 0.4, w * 0.5), DSTEEL, p).position.y = grip;
  const s = new THREE.Shape();
  const y0 = grip + w * 0.15;
  s.moveTo(-w / 2, y0);
  s.lineTo(w / 2, y0);
  s.lineTo(w * 0.42, y0 + len * 0.82);
  s.lineTo(0, y0 + len);
  s.lineTo(-w * 0.42, y0 + len * 0.82);
  s.closePath();
  plate(p, s, w * 0.12, BLADE);
  M(new THREE.BoxGeometry(w * 0.18, len * 0.7, w * 0.14 + 0.012), DSTEEL, p).position.y = y0 + len * 0.38;
};

/** A flat extruded plate in the XY plane, centred in depth. */
const plate = (p: THREE.Object3D, s: THREE.Shape, depth: number, mat: THREE.Material): THREE.Mesh => {
  const m = M(new THREE.ExtrudeGeometry(s, { depth, bevelEnabled: true, bevelThickness: 0.008, bevelSize: 0.008, bevelSegments: 1, curveSegments: 6 }), mat, p);
  m.position.z = -depth / 2;
  return m;
};

/** An axe blade on the +X side of a haft: `w` wide, `h` tall at the edge, `bh` at the beard. */
const axeBlade = (w: number, h: number, beard = 0): THREE.Shape => {
  const s = new THREE.Shape();
  s.moveTo(0, h * 0.28);
  s.quadraticCurveTo(w * 0.55, h * 0.3, w, h * 0.55);
  s.quadraticCurveTo(w * 1.12, 0, w, -h * 0.5 - beard);
  s.quadraticCurveTo(w * 0.5, -h * 0.25, 0, -h * 0.22);
  s.closePath();
  return s;
};

/** A curved tube through points. */
const tube = (p: THREE.Object3D, pts: THREE.Vector3[], r: number, mat: THREE.Material, seg = 14, radial = 5): THREE.Mesh =>
  M(new THREE.TubeGeometry(new THREE.CatmullRomCurve3(pts), seg, r, radial), mat, p);

/** A bow: limbs bending back (-X), string across, leather grip. `h` tall, `d` deep. */
const bow = (p: THREE.Object3D, h: number, d: number, r: number, recurve: boolean): void => {
  const pts: THREE.Vector3[] = [];
  for (let i = 0; i <= 10; i++) {
    const t = i / 10 - 0.5;
    const tipK = recurve && Math.abs(t) > 0.38 ? (Math.abs(t) - 0.38) * 1.6 : 0;
    pts.push(V(-Math.cos(t * Math.PI) * d + tipK * d * 2.2, t * h, 0));
  }
  tube(p, pts, r, DWOOD, 20, 5);
  limb(p, pts[0]!, pts[10]!, 0.005, 0.005, STRING, 3);
  M(new THREE.CylinderGeometry(r * 1.4, r * 1.4, h * 0.12, 6), LEATHER_BELT, p).position.set(-d, 0, 0);
  for (const k of [0, 10]) M(new THREE.SphereGeometry(r * 1.2, 5, 4), DSTEEL, p).position.copy(pts[k]!);
};

/** The torso on its stand (the uniques' armour), built in `p`. Returns the radius at height y. */
const torsoR = (y: number): number => (y < -0.15 ? 0.2 : y < 0.05 ? 0.17 + (y + 0.15) * 0.25 : y < 0.2 ? 0.22 : 0.22 - (y - 0.2) * 1.4);
const torso = (p: THREE.Object3D, mat: THREE.Material, k = 1): THREE.Mesh => {
  const pts: THREE.Vector2[] = [];
  for (let i = 0; i <= 12; i++) {
    const y = -0.3 + i * 0.05;
    pts.push(new THREE.Vector2(Math.max(0.06, torsoR(y)) * k, y));
  }
  const m = M(new THREE.LatheGeometry(pts, 14), mat, p);
  m.scale.z = 0.68;
  return m;
};
/** Rows of rings round the torso (mail). */
const mailRows = (p: THREE.Object3D, y0: number, y1: number, n: number, k: number, mat: THREE.Material): void => {
  for (let i = 0; i < n; i++) {
    const y = y0 + ((y1 - y0) * i) / (n - 1);
    const r = M(new THREE.TorusGeometry(Math.max(0.07, torsoR(y)) * k, 0.008, 3, 20), mat, p);
    r.position.y = y;
    r.rotation.x = Math.PI / 2;
    r.scale.y = 0.68;
  }
};

/** One boot in profile: toe toward +X. `soft` folds the shaft; `cuff` adds a turned-down top. */
const boot = (p: THREE.Object3D, mat: THREE.Material, o: { shaft?: number; cuff?: THREE.Material; toe?: THREE.Material; sole?: THREE.Material } = {}): THREE.Group => {
  const b = G(p);
  const sh = o.shaft ?? 0.34;
  const leg = M(new THREE.CylinderGeometry(0.085, 0.075, sh, 10), mat, b);
  leg.position.y = 0.06 + sh / 2;
  const foot = M(new THREE.SphereGeometry(0.09, 10, 6), o.toe ?? mat, b);
  foot.scale.set(1.75, 0.7, 0.95);
  foot.position.set(0.07, 0.06, 0);
  const heel = M(new THREE.CylinderGeometry(0.08, 0.08, 0.08, 10), mat, b);
  heel.position.set(-0.01, 0.07, 0);
  const sole = M(new THREE.BoxGeometry(0.3, 0.025, 0.15), o.sole ?? SOLE, b);
  sole.position.set(0.05, 0.012, 0);
  if (o.cuff) {
    const c = M(new THREE.CylinderGeometry(0.1, 0.092, 0.07, 10), o.cuff, b);
    c.position.y = 0.06 + sh - 0.02;
  }
  return b;
};

/** A cord loop the amulet hangs from (above the origin). */
const cord = (p: THREE.Object3D, mat: THREE.Material, n = 11, w = 0.13, h = 0.16): void => {
  for (let i = 0; i < n; i++) {
    const a = (i / (n - 1)) * Math.PI * 1.3 - Math.PI * 0.65;
    M(new THREE.SphereGeometry(0.012, 4, 3), mat, p).position.set(Math.sin(a) * w, (1 - Math.cos(a)) * h + 0.02, 0);
  }
};

/** A ring standing up, facing +Z, its setting on top (at y = r). */
const hoop = (p: THREE.Object3D, r: number, tube_: number, mat: THREE.Material, flat = 1): THREE.Mesh => {
  const m = M(new THREE.TorusGeometry(r, tube_, 6, 24), mat, p);
  m.scale.z = flat;
  return m;
};

/** Builds the bare piece of a base under `it`; returns where its rarity marks go. */
const build = (base: GearBaseId, it: THREE.Group): Piece => {
  switch (base) {
    /* ---- one hand ---- */
    case 'longsword': {
      const sw = G(it, 0, -0.4, 0);
      longBlade(sw, 0.62, 0.085, 0.16, 0.2);
      return { at: sw, rune: [V(0, 0.3, 0.014), V(0, 0.62, 0.014)], gem: V(0, 0.16, 0.03) };
    }
    case 'handaxe': {
      const a = G(it, 0, -0.38, 0);
      haft(a, 0, 0.78, 0.022);
      const hd = G(a, 0, 0.68, 0);
      plate(hd, axeBlade(0.2, 0.2, 0.04), 0.03, BLADE);
      M(new THREE.BoxGeometry(0.06, 0.1, 0.06), DSTEEL, hd);
      return { at: hd, rune: [V(0.08, 0.04, 0.02), V(0.13, -0.08, 0.02)], gem: V(0, 0, 0.035), rs: 0.8 };
    }
    case 'mace': {
      const a = G(it, 0, -0.38, 0);
      haft(a, 0, 0.66, 0.022, DWOOD);
      const hd = G(a, 0, 0.72, 0);
      M(new THREE.SphereGeometry(0.065, 8, 6), DSTEEL, hd);
      for (let i = 0; i < 6; i++) {
        const ang = (i / 6) * Math.PI * 2;
        const f = M(new THREE.BoxGeometry(0.11, 0.17, 0.022), metal('#9aa2ab', { roughness: 0.3, flatShading: true }), hd);
        f.position.set(Math.cos(ang) * 0.07, 0, Math.sin(ang) * 0.07);
        f.rotation.y = -ang;
      }
      M(new THREE.ConeGeometry(0.03, 0.07, 5), DSTEEL, hd).position.y = 0.11;
      M(new THREE.CylinderGeometry(0.035, 0.03, 0.05, 6), DSTEEL, a).position.y = 0.62;
      return { at: a, rune: [V(0, 0.3, 0.024), V(0, 0.5, 0.024)], gem: V(0, 0.72, 0.13), rs: 0.8 };
    }
    case 'warhammer': {
      const a = G(it, 0, -0.38, 0);
      haft(a, 0, 0.78, 0.022);
      const hd = G(a, 0, 0.72, 0);
      M(new THREE.BoxGeometry(0.2, 0.12, 0.12), DSTEEL, hd).position.x = 0.04;
      M(new THREE.BoxGeometry(0.03, 0.14, 0.14), STEEL, hd).position.x = 0.15;
      const sp = M(new THREE.ConeGeometry(0.04, 0.16, 4), STEEL, hd);
      sp.rotation.z = Math.PI / 2;
      sp.position.x = -0.13;
      M(new THREE.ConeGeometry(0.025, 0.08, 4), STEEL, hd).position.y = 0.1;
      return { at: hd, rune: [V(-0.01, 0, 0.062), V(0.09, 0, 0.062)], gem: V(0.04, 0, 0.065), rs: 0.8 };
    }
    case 'mossHood': {
      // The moss hood (docs/SABERES.md §4.4): the leather hood lined and rimmed with living moss, a tuft at its
      // point, a sprig of pale lichen pinned at the throat. It hides the face from the ones that shoot from afar.
      const pc = build('hood', it);
      const b = pc.at;
      for (let i = 0; i < 11; i++) {
        const a = -1.25 + (i / 10) * 2.5;
        const tuft = M(new THREE.DodecahedronGeometry(0.035 + (i % 3) * 0.008, 0), i % 2 ? MOSS : MOSS_DK, b);
        tuft.position.set(Math.sin(a) * 0.19, 0.06 + Math.cos(a) * 0.2, 0.12 + Math.cos(a) * 0.05);
        tuft.scale.set(1, 0.7, 0.8);
      }
      for (let i = 0; i < 8; i++) {
        const a = (i / 8) * Math.PI * 2;
        const m = M(new THREE.DodecahedronGeometry(0.04, 0), i % 2 ? MOSS : MOSS_DK, b);
        m.position.set(Math.sin(a) * 0.3, -0.3, Math.cos(a) * 0.26);
        m.scale.set(1.2, 0.6, 1);
      }
      M(new THREE.DodecahedronGeometry(0.05, 0), MOSS, b).position.set(0, 0.19, -0.35);
      const lichen = M(new THREE.OctahedronGeometry(0.03, 0), LICHEN, b);
      lichen.position.set(0.05, -0.12, 0.25);
      return pc;
    }
    case 'absentCloak': {
      // The absent's cloak (docs/SABERES.md §4.4): the long grey cloak the old garrison put over those on watch far
      // away, its hood down, falling to the ground in heavy folds, a dark hem, the Vigil's eye on the clasp.
      const b = G(it, 0, 0.02, 0);
      const body = M(new THREE.CylinderGeometry(0.17, 0.36, 0.95, 14, 1, true, 0.35, Math.PI * 2 - 0.7), WATCH, b);
      body.position.y = -0.3;
      body.scale.z = 0.8;
      for (let i = 0; i < 6; i++) {
        const a = 0.6 + (i / 5) * (Math.PI * 2 - 1.2);
        const fold = M(new THREE.CylinderGeometry(0.018, 0.035, 0.9, 5), WATCH_DK, b);
        fold.position.set(Math.sin(a) * 0.27, -0.32, Math.cos(a) * 0.22);
        fold.rotation.set(Math.cos(a) * -0.18, 0, Math.sin(a) * 0.18);
      }
      const hem = M(new THREE.TorusGeometry(0.36, 0.02, 3, 22, Math.PI * 2 - 0.7), WATCH_DK, b);
      hem.rotation.set(Math.PI / 2, 0, Math.PI / 2 + 0.35);
      hem.position.y = -0.77;
      hem.scale.y = 0.8;
      // The hood lying back on the shoulders, and the collar.
      const hood = M(new THREE.SphereGeometry(0.16, 10, 6, 0, Math.PI * 2, 0, Math.PI * 0.6), WATCH, b);
      hood.position.set(0, 0.16, -0.1);
      hood.rotation.x = -1.9;
      const collar = M(new THREE.TorusGeometry(0.14, 0.04, 4, 14), WATCH_DK, b);
      collar.rotation.x = Math.PI / 2;
      collar.position.y = 0.18;
      // The clasp: a dark iron disc with the Vigil's pale eye.
      const clasp = M(new THREE.CylinderGeometry(0.045, 0.045, 0.02, 10), DSTEEL, b);
      clasp.rotation.x = Math.PI / 2;
      clasp.position.set(0, 0.15, 0.15);
      const eye = M(new THREE.SphereGeometry(0.018, 6, 4), VIGIL_EYE, b);
      eye.position.set(0, 0.15, 0.163);
      eye.scale.set(1.5, 0.7, 0.5);
      return { at: b, rune: [V(-0.2, -0.5, 0.25), V(0.2, -0.5, 0.25)], gem: V(0, 0.15, 0.17), rs: 0.8 };
    }
    case 'masonHammer': {
      // A stonemason's hammer of the old garrison: a squared iron head, one face dressed stone-grey and bush-cut,
      // the other drawn to a chisel edge, on an ash haft bound with cord; a plumb-line hook under the head.
      const a = G(it, 0, -0.38, 0);
      haft(a, 0, 0.74, 0.021);
      const hd = G(a, 0, 0.72, 0);
      M(new THREE.BoxGeometry(0.17, 0.1, 0.1), DSTEEL, hd);
      const face = M(new THREE.BoxGeometry(0.05, 0.12, 0.12), MASON_FACE, hd);
      face.position.x = 0.105;
      for (let i = 0; i < 3; i++)
        for (let j = 0; j < 3; j++) {
          const n = M(new THREE.BoxGeometry(0.012, 0.026, 0.026), MASON_FACE, hd);
          n.position.set(0.135, -0.034 + i * 0.034, -0.034 + j * 0.034);
        }
      const edge = M(new THREE.BoxGeometry(0.11, 0.1, 0.03), STEEL, hd);
      edge.position.x = -0.12;
      edge.scale.set(1, 1, 0.6);
      M(new THREE.TorusGeometry(0.018, 0.005, 4, 10), DSTEEL, hd).position.set(0, -0.07, 0);
      M(new THREE.CylinderGeometry(0.027, 0.024, 0.07, 6), DSTEEL, a).position.y = 0.64;
      return { at: hd, rune: [V(-0.04, 0, 0.052), V(0.06, 0, 0.052)], gem: V(0.01, 0, 0.055), rs: 0.75 };
    }
    case 'wand': {
      const a = G(it, 0, -0.34, 0);
      limb(a, V(0, 0, 0), V(0, 0.62, 0), 0.032, 0.02, DWOOD, 6);
      for (const y of [0.1, 0.16, 0.5]) M(new THREE.CylinderGeometry(0.03, 0.03, 0.02, 6), DSTEEL, a).position.y = y;
      M(new THREE.SphereGeometry(0.032, 6, 4), DSTEEL, a).position.y = 0;
      for (let i = 0; i < 3; i++) {
        const ang = (i / 3) * Math.PI * 2;
        limb(a, V(0, 0.6, 0), V(Math.cos(ang) * 0.035, 0.7, Math.sin(ang) * 0.035), 0.008, 0.005, DSTEEL, 3);
      }
      const cr = M(new THREE.OctahedronGeometry(0.06), emis('#d8e4f0', '#6a8cb8', 0.5, { flatShading: true }), a);
      cr.position.y = 0.7;
      cr.scale.y = 1.6;
      return { at: a, rune: [V(0, 0.24, 0.022), V(0, 0.44, 0.02)], gem: V(0, 0.7, 0), rs: 0.7 };
    }
    case 'sickle': {
      const a = G(it, 0, -0.36, 0);
      limb(a, V(0, 0, 0), V(0, 0.32, 0), 0.028, 0.026, HAFT, 6);
      M(new THREE.CylinderGeometry(0.032, 0.032, 0.18, 6), LEATHER_BELT, a).position.y = 0.12;
      M(new THREE.SphereGeometry(0.03, 6, 4), DSTEEL, a);
      M(new THREE.CylinderGeometry(0.036, 0.03, 0.05, 6), DSTEEL, a).position.y = 0.33;
      // The crescent: up from the ferrule, over and forward to a point hanging toward +X.
      const cr = new THREE.Shape();
      cr.moveTo(-0.03, 0.32);
      cr.quadraticCurveTo(-0.08, 0.78, 0.26, 0.8);
      cr.quadraticCurveTo(0.44, 0.8, 0.5, 0.58);
      cr.quadraticCurveTo(0.38, 0.7, 0.25, 0.7);
      cr.quadraticCurveTo(0.04, 0.68, 0.04, 0.32);
      cr.closePath();
      plate(a, cr, 0.022, BLADE);
      return { at: a, rune: [V(-0.01, 0.48, 0.02), V(0.2, 0.75, 0.02)], gem: V(0, 0.33, 0.04), rs: 0.7 };
    }
    case 'shortbow': {
      const a = G(it, 0.08, 0, 0);
      bow(a, 0.82, 0.13, 0.02, true);
      limb(a, V(-0.13, 0, 0), V(0.3, 0, 0), 0.006, 0.006, WOOD, 4);
      const tip = M(new THREE.ConeGeometry(0.02, 0.06, 4), STEEL, a);
      tip.rotation.z = -Math.PI / 2;
      tip.position.x = 0.33;
      return { at: a, rune: [V(-0.115, 0.14, 0.02), V(-0.07, 0.32, 0.02)], gem: V(-0.13, 0, 0.03), rs: 0.7 };
    }
    /* ---- two hands: bigger, heavier ---- */
    case 'greatsword': {
      // Longer, broader and thicker than the longsword, with a two-hand grip and a heavy cross.
      const sw = G(it, 0, -0.66, 0);
      longBlade(sw, 0.98, 0.13, 0.32, 0.34);
      for (const k of [-1, 1]) M(new THREE.SphereGeometry(0.03, 6, 4), DSTEEL, sw).position.set(k * 0.17, 0.32, 0);
      return { at: sw, rune: [V(0, 0.52, 0.018), V(0, 1.06, 0.018)], gem: V(0, 0.32, 0.035) };
    }
    case 'greataxe': {
      const a = G(it, 0, -0.62, 0);
      haft(a, 0, 1.32, 0.032);
      const hd = G(a, 0, 1.12, 0);
      for (const k of [-1, 1]) {
        const s = G(hd);
        s.scale.x = k;
        plate(s, axeBlade(0.36, 0.42, 0.04), 0.035, BLADE);
      }
      M(new THREE.BoxGeometry(0.1, 0.22, 0.08), DSTEEL, hd);
      M(new THREE.ConeGeometry(0.035, 0.12, 4), DSTEEL, hd).position.y = 0.18;
      return { at: hd, rune: [V(0.14, 0.08, 0.025), V(0.22, -0.12, 0.025)], gem: V(0, 0, 0.045) };
    }
    case 'maul': {
      const a = G(it, 0, -0.62, 0);
      haft(a, 0, 1.22, 0.032, DWOOD);
      const hd = G(a, 0, 1.18, 0);
      const hm = metal('#7c838c', { roughness: 0.4 });
      M(new THREE.BoxGeometry(0.46, 0.28, 0.28), hm, hd);
      for (const k of [-1, 1]) M(new THREE.BoxGeometry(0.05, 0.31, 0.31), DSTEEL, hd).position.x = k * 0.215;
      M(new THREE.BoxGeometry(0.1, 0.3, 0.3), DSTEEL, hd);
      return { at: hd, rune: [V(-0.12, 0, 0.132), V(0.12, 0, 0.132)], gem: V(0, 0, 0.135) };
    }
    case 'staff': {
      const a = G(it, 0, -0.68, 0);
      limb(a, V(0, 0, 0), V(0, 1.18, 0), 0.026, 0.032, DWOOD, 6);
      M(new THREE.CylinderGeometry(0.04, 0.04, 0.16, 6), LEATHER_BELT, a).position.y = 0.6;
      M(new THREE.ConeGeometry(0.03, 0.06, 6), DSTEEL, a).rotation.x = Math.PI;
      M(new THREE.CylinderGeometry(0.045, 0.035, 0.06, 6), DSTEEL, a).position.y = 1.16;
      for (let i = 0; i < 4; i++) {
        const ang = (i / 4) * Math.PI * 2 + 0.4;
        tube(a, [V(0, 1.16, 0), V(Math.cos(ang) * 0.15, 1.28, Math.sin(ang) * 0.15), V(Math.cos(ang) * 0.08, 1.5, Math.sin(ang) * 0.08)], 0.016, DSTEEL, 6, 4);
      }
      const cr = M(new THREE.OctahedronGeometry(0.14), emis('#d6ecff', '#3a7fd0', 0.8, { flatShading: true }), a);
      cr.position.y = 1.36;
      cr.scale.y = 1.5;
      return { at: a, rune: [V(0, 0.75, 0.032), V(0, 1.05, 0.034)], gem: V(0, 0.6, 0.045) };
    }
    case 'quarterstaff': {
      const a = G(it, 0, -0.7, 0);
      const pts: THREE.Vector3[] = [];
      for (let i = 0; i <= 8; i++) pts.push(V(Math.sin(i * 1.7) * 0.012, i * 0.17, Math.cos(i * 1.3) * 0.012));
      tube(a, pts, 0.034, WOOD, 16, 6);
      // The gnarled head: a knot curling over, a sprig of leaves.
      tube(a, [V(0, 1.3, 0), V(0.06, 1.42, 0), V(0.0, 1.5, 0), V(-0.07, 1.44, 0), V(-0.04, 1.36, 0)], 0.032, WOOD, 10, 6);
      M(new THREE.SphereGeometry(0.05, 6, 5), WOOD, a).position.set(0, 1.3, 0);
      const leaf = std('#4f6b34', { roughness: 0.9, flatShading: true, side: THREE.DoubleSide });
      for (let i = 0; i < 4; i++) {
        const l = M(new THREE.SphereGeometry(0.04, 5, 3), leaf, a);
        l.scale.set(0.45, 1, 0.2);
        l.position.set(0.05 + i * 0.012, 1.24 - i * 0.05, 0.03);
        l.rotation.z = -0.9 + i * 0.25;
      }
      for (const y of [0.62, 0.86]) M(new THREE.CylinderGeometry(0.042, 0.042, 0.05, 6), LEATHER_BELT, a).position.y = y;
      for (const y of [0.3, 1.05]) M(new THREE.SphereGeometry(0.044, 6, 4), WOOD, a).position.y = y;
      return { at: a, rune: [V(0, 0.95, 0.036), V(0, 1.2, 0.036)], gem: V(0, 1.42, 0.04) };
    }
    case 'longbow': {
      const a = G(it, 0.12, 0, 0);
      bow(a, 1.45, 0.18, 0.027, false);
      limb(a, V(-0.18, 0, 0), V(0.42, 0, 0), 0.007, 0.007, WOOD, 4);
      const tip = M(new THREE.ConeGeometry(0.024, 0.07, 4), STEEL, a);
      tip.rotation.z = -Math.PI / 2;
      tip.position.x = 0.45;
      return { at: a, rune: [V(-0.16, 0.2, 0.026), V(-0.1, 0.5, 0.026)], gem: V(-0.18, 0, 0.035) };
    }
    case 'heavyCrossbow': {
      // Seen from above-front: a long stock toward +Y, a wide steel prod, a windlass at the butt.
      const a = G(it, 0, -0.5, 0);
      M(new THREE.BoxGeometry(0.11, 0.95, 0.09), HAFT, a).position.y = 0.48;
      M(new THREE.BoxGeometry(0.14, 0.24, 0.12), HAFT, a).position.y = 0.06;
      M(new THREE.BoxGeometry(0.035, 0.9, 0.02), DSTEEL, a).position.set(0, 0.5, 0.05);
      const prodPts: THREE.Vector3[] = [];
      for (let i = 0; i <= 8; i++) {
        const t = i / 8 - 0.5;
        prodPts.push(V(t * 0.95, 0.88 - Math.abs(t) * Math.abs(t) * 0.5, 0.02));
      }
      tube(a, prodPts, 0.026, STEEL, 16, 5);
      limb(a, prodPts[0]!, V(0, 0.55, 0.06), 0.006, 0.006, STRING, 3);
      limb(a, prodPts[8]!, V(0, 0.55, 0.06), 0.006, 0.006, STRING, 3);
      M(new THREE.BoxGeometry(0.16, 0.1, 0.12), DSTEEL, a).position.y = 0.9;
      // The bolt on the rail.
      limb(a, V(0, 0.56, 0.07), V(0, 1.02, 0.07), 0.01, 0.01, WOOD, 4);
      M(new THREE.ConeGeometry(0.022, 0.07, 4), STEEL, a).position.set(0, 1.05, 0.07);
      // Windlass with two cranks.
      limb(a, V(-0.13, 0.05, 0), V(0.13, 0.05, 0), 0.03, 0.03, DSTEEL, 6);
      for (const k of [-1, 1]) {
        limb(a, V(k * 0.14, 0.05, 0), V(k * 0.14, -0.06, 0.06), 0.01, 0.01, DSTEEL, 3);
        M(new THREE.SphereGeometry(0.022, 5, 4), HAFT, a).position.set(k * 0.14, -0.06, 0.06);
      }
      a.rotation.x = -0.35;
      return { at: a, rune: [V(-0.04, 0.25, 0.064), V(-0.04, 0.42, 0.064)], gem: V(0, 0.9, 0.065) };
    }
    /* ---- off hand ---- */
    case 'shield': {
      const s = G(it);
      const rim = heaterShield(0.6, 0.76, DSTEEL);
      s.add(rim);
      rim.position.z = -0.03;
      const face = heaterShield(0.53, 0.68, metal('#33415a', { roughness: 0.5 }));
      s.add(face);
      face.position.z = 0.0;
      // A pale chevron across the field and a steel boss.
      const ch = new THREE.Shape();
      ch.moveTo(-0.26, -0.02);
      ch.lineTo(0, 0.16);
      ch.lineTo(0.26, -0.02);
      ch.lineTo(0.26, -0.12);
      ch.lineTo(0, 0.06);
      ch.lineTo(-0.26, -0.12);
      ch.closePath();
      plate(s, ch, 0.01, std('#c8ccd2', { roughness: 0.6 })).position.z = 0.055;
      M(new THREE.SphereGeometry(0.06, 8, 5, 0, Math.PI * 2, 0, Math.PI / 2), STEEL, s).rotation.x = Math.PI / 2;
      (s.children[s.children.length - 1] as THREE.Mesh).position.set(0, -0.12, 0.05);
      return { at: s, rune: [V(-0.15, 0.25, 0.065), V(0.15, 0.25, 0.065)], gem: V(0, -0.12, 0.11) };
    }
    case 'offAxe': {
      // The barbarian's second axe: a short bearded hatchet, a hook on the back, the haft wrapped in hide.
      const a = G(it, 0, -0.36, 0);
      limb(a, V(0, 0, 0), V(0, 0.72, 0), 0.024, 0.024, DWOOD, 6);
      M(new THREE.CylinderGeometry(0.032, 0.032, 0.28, 6), leather('#6a4a32', { bumpScale: 0.04 }), a).position.y = 0.18;
      M(new THREE.SphereGeometry(0.035, 6, 4), DSTEEL, a);
      const hd = G(a, 0, 0.6, 0);
      plate(hd, axeBlade(0.22, 0.14, 0.16), 0.03, metal('#8e959d', { roughness: 0.35 }));
      const hook = new THREE.Shape();
      hook.moveTo(0, 0.04);
      hook.quadraticCurveTo(-0.1, 0.05, -0.15, -0.06);
      hook.quadraticCurveTo(-0.08, -0.01, 0, -0.03);
      hook.closePath();
      plate(hd, hook, 0.025, DSTEEL);
      M(new THREE.BoxGeometry(0.06, 0.1, 0.06), DSTEEL, hd);
      for (let i = 0; i < 2; i++) M(new THREE.SphereGeometry(0.02, 5, 4), std('#d8ccb0'), a).position.set(0.03, 0.4 - i * 0.05, 0.02);
      return { at: hd, rune: [V(0.09, 0.0, 0.02), V(0.14, -0.14, 0.02)], gem: V(0, 0, 0.035), rs: 0.8 };
    }
    case 'grimoire': {
      const b = G(it, 0, 0.02, 0);
      b.rotation.x = -0.3;
      const cov = leather('#4a2626');
      M(new THREE.BoxGeometry(0.46, 0.6, 0.04), cov, b).position.z = 0.07;
      M(new THREE.BoxGeometry(0.46, 0.6, 0.04), cov, b).position.z = -0.07;
      M(new THREE.BoxGeometry(0.43, 0.56, 0.11), std('#d6d4ce', { roughness: 1 }), b).position.x = 0.01;
      M(new THREE.BoxGeometry(0.06, 0.6, 0.18), cov, b).position.x = -0.22;
      // Iron corners, a clasp and a ring sigil on the cover.
      for (const [x, y] of [[0.2, 0.27], [0.2, -0.27], [-0.18, 0.27], [-0.18, -0.27]] as const) M(new THREE.BoxGeometry(0.07, 0.07, 0.05), DSTEEL, b).position.set(x, y, 0.075);
      M(new THREE.BoxGeometry(0.03, 0.08, 0.17), DSTEEL, b).position.set(0.235, 0, 0);
      const sig = M(new THREE.TorusGeometry(0.1, 0.014, 4, 18), DSTEEL, b);
      sig.position.z = 0.092;
      for (let i = 0; i < 3; i++) {
        const l = M(new THREE.BoxGeometry(0.012, 0.2, 0.01), DSTEEL, b);
        l.position.z = 0.093;
        l.rotation.z = (i * Math.PI) / 3;
      }
      return { at: b, rune: [V(-0.12, -0.2, 0.094), V(0.12, -0.2, 0.094)], gem: V(0, 0, 0.1) };
    }
    case 'orb': {
      const b = G(it, 0, -0.1, 0);
      M(new THREE.CylinderGeometry(0.1, 0.14, 0.06, 8), DSTEEL, b).position.y = -0.2;
      limb(b, V(0, -0.17, 0), V(0, -0.08, 0), 0.04, 0.05, DSTEEL, 6);
      for (let i = 0; i < 4; i++) {
        const ang = (i / 4) * Math.PI * 2 + 0.3;
        tube(b, [V(0, -0.08, 0), V(Math.cos(ang) * 0.17, 0.0, Math.sin(ang) * 0.17), V(Math.cos(ang) * 0.15, 0.17, Math.sin(ang) * 0.15), V(Math.cos(ang) * 0.06, 0.25, Math.sin(ang) * 0.06)], 0.014, DSTEEL, 8, 4);
      }
      M(new THREE.SphereGeometry(0.18, 14, 10), emis('#6f5f8f', '#3a2a6a', 0.45, { roughness: 0.15, metalness: 0.2 }), b).position.y = 0.1;
      M(new THREE.SphereGeometry(0.07, 8, 6), emis('#e8ddff', '#9a7aff', 0.9), b).position.set(0.03, 0.12, 0.08);
      return { at: b, rune: [V(-0.09, -0.2, 0.135), V(0.09, -0.2, 0.135)], gem: V(0, 0.1, 0.2) };
    }
    case 'holySymbol': {
      // A sun disc on a short haft: flat rays round a raised boss.
      const b = G(it, 0, 0.06, 0);
      const sil = metal('#d4d8de', { roughness: 0.25 });
      limb(b, V(0, -0.52, 0), V(0, -0.2, 0), 0.026, 0.026, DWOOD, 6);
      M(new THREE.SphereGeometry(0.035, 6, 4), DSTEEL, b).position.y = -0.52;
      const sun = new THREE.Shape();
      for (let i = 0; i < 24; i++) {
        const ang = (i / 24) * Math.PI * 2 + Math.PI / 2;
        const r = i % 2 === 1 ? 0.2 : i % 4 === 0 ? 0.36 : 0.29;
        const x = Math.cos(ang) * r, y = Math.sin(ang) * r;
        if (i === 0) sun.moveTo(x, y);
        else sun.lineTo(x, y);
      }
      sun.closePath();
      plate(b, sun, 0.03, sil);
      const disc = M(new THREE.CylinderGeometry(0.17, 0.17, 0.05, 18), DSTEEL, b);
      disc.rotation.x = Math.PI / 2;
      const boss = M(new THREE.SphereGeometry(0.1, 12, 6, 0, Math.PI * 2, 0, Math.PI / 2), sil, b);
      boss.rotation.x = Math.PI / 2;
      boss.position.z = 0.02;
      boss.scale.y = 0.6;
      return { at: b, arc: { r: 0.135, z: 0.03, a0: -2.6, a1: -0.54 }, gem: V(0, 0, 0.09), rs: 0.8 };
    }
    case 'quiver': {
      const b = G(it, 0, -0.05, 0);
      b.rotation.z = 0.25;
      const q = M(new THREE.CylinderGeometry(0.1, 0.08, 0.62, 10), HIDE, b);
      q.position.y = -0.05;
      M(new THREE.CylinderGeometry(0.085, 0.07, 0.03, 10), HIDE_D, b).position.y = -0.37;
      for (const y of [0.22, -0.28]) M(new THREE.TorusGeometry(0.1, 0.012, 4, 14), HIDE_D, b).position.y = y;
      for (const m of b.children.slice(-2)) m.rotation.x = Math.PI / 2;
      const strap = M(new THREE.BoxGeometry(0.04, 0.7, 0.015), LEATHER_BELT, b);
      strap.position.set(0, -0.02, -0.1);
      strap.rotation.z = 0.15;
      const fl = std('#d8d4cc', { roughness: 0.9, side: THREE.DoubleSide });
      const fl2 = std('#7a2e2a', { roughness: 0.9, side: THREE.DoubleSide });
      for (let i = 0; i < 6; i++) {
        const x = ((i % 3) - 1) * 0.05, z = (i < 3 ? 0.03 : -0.03);
        const h = 0.36 + (i % 2) * 0.05;
        limb(b, V(x, 0.2, z), V(x * 1.3, h, z), 0.007, 0.007, WOOD, 3);
        const f = M(new THREE.PlaneGeometry(0.035, 0.1), i % 2 ? fl2 : fl, b);
        f.position.set(x * 1.3, h - 0.03, z);
        const f2 = M(new THREE.PlaneGeometry(0.035, 0.1), i % 2 ? fl2 : fl, b);
        f2.position.set(x * 1.3, h - 0.03, z);
        f2.rotation.y = Math.PI / 2;
      }
      return { at: b, rune: [V(0, 0.08, 0.1), V(0, -0.2, 0.09)], gem: V(0, 0.22, 0.11) };
    }
    case 'toolkit': {
      const b = G(it, 0, -0.12, 0);
      const box = std('#5b3d26', { roughness: 0.85 });
      M(new THREE.BoxGeometry(0.56, 0.26, 0.26), box, b);
      for (const x of [-0.2, 0.2]) M(new THREE.BoxGeometry(0.04, 0.27, 0.27), DSTEEL, b).position.x = x;
      M(new THREE.BoxGeometry(0.1, 0.06, 0.02), DSTEEL, b).position.set(0, 0.06, 0.135);
      limb(b, V(-0.12, 0.13, 0), V(-0.12, 0.25, 0), 0.012, 0.012, DSTEEL, 4);
      limb(b, V(0.12, 0.13, 0), V(0.12, 0.25, 0), 0.012, 0.012, DSTEEL, 4);
      limb(b, V(-0.13, 0.25, 0), V(0.13, 0.25, 0), 0.014, 0.014, DSTEEL, 4);
      // Tools sticking out: a hammer, a wrench, a file, and a gear on the side.
      limb(b, V(-0.2, 0.1, 0.06), V(-0.27, 0.4, 0.06), 0.014, 0.014, WOOD, 5);
      const hh = M(new THREE.BoxGeometry(0.14, 0.05, 0.05), DSTEEL, b);
      hh.position.set(-0.27, 0.42, 0.06);
      hh.rotation.z = -0.23;
      limb(b, V(0.18, 0.1, -0.05), V(0.24, 0.38, -0.05), 0.012, 0.012, STEEL, 4);
      const wr = M(new THREE.TorusGeometry(0.035, 0.014, 4, 10, Math.PI * 1.5), STEEL, b);
      wr.position.set(0.245, 0.41, -0.05);
      wr.rotation.z = 0.8;
      limb(b, V(0.04, 0.1, 0.0), V(0.06, 0.34, 0.0), 0.01, 0.006, DSTEEL, 4);
      const gr = M(new THREE.TorusGeometry(0.06, 0.022, 4, 12), metal('#8c5a2b', { roughness: 0.45 }), b);
      gr.position.set(0.08, -0.04, 0.14);
      for (let i = 0; i < 8; i++) {
        const ang = (i / 8) * Math.PI * 2;
        M(new THREE.BoxGeometry(0.025, 0.025, 0.035), metal('#8c5a2b', { roughness: 0.45 }), b).position.set(0.08 + Math.cos(ang) * 0.085, -0.04 + Math.sin(ang) * 0.085, 0.14);
      }
      return { at: b, rune: [V(-0.15, -0.06, 0.135), V(-0.03, -0.06, 0.135)], gem: V(0.08, -0.04, 0.16), rs: 0.8 };
    }
    case 'totem': {
      // A carved post: two stacked faces, a beak, feathers hanging off the side.
      const b = G(it, 0, -0.4, 0);
      const wd = std('#6a4a30', { roughness: 0.9, flatShading: true });
      const dk = std('#1a1412');
      M(new THREE.CylinderGeometry(0.11, 0.13, 0.8, 8), wd, b).position.y = 0.4;
      for (const [y, s] of [[0.62, 1], [0.3, 0.9]] as const) {
        M(new THREE.BoxGeometry(0.2 * s, 0.03, 0.06), wd, b).position.set(0, y + 0.05, 0.1);
        for (const k of [-1, 1]) M(new THREE.SphereGeometry(0.026 * s, 5, 4), dk, b).position.set(k * 0.05 * s, y + 0.01, 0.115);
        M(new THREE.BoxGeometry(0.1 * s, 0.02, 0.02), dk, b).position.set(0, y - 0.08, 0.12);
      }
      const beak = M(new THREE.ConeGeometry(0.035, 0.12, 4), wd, b);
      beak.rotation.x = Math.PI / 2;
      beak.position.set(0, 0.58, 0.16);
      for (const k of [-1, 1]) {
        const w = M(new THREE.BoxGeometry(0.16, 0.08, 0.03), wd, b);
        w.position.set(k * 0.17, 0.74, 0);
        w.rotation.z = k * 0.35;
      }
      const fe = std('#d8d0bd', { roughness: 1, side: THREE.DoubleSide });
      for (let i = 0; i < 3; i++) {
        limb(b, V(0.12, 0.5, 0.02), V(0.17 + i * 0.02, 0.36 - i * 0.04, 0.03), 0.005, 0.005, LEATHER_BELT, 3);
        const f = M(new THREE.SphereGeometry(0.035, 5, 3), i === 1 ? std('#3a2a24', { side: THREE.DoubleSide }) : fe, b);
        f.scale.set(0.4, 1.4, 0.15);
        f.position.set(0.17 + i * 0.02, 0.3 - i * 0.04, 0.03);
      }
      return { at: b, rune: [V(-0.05, 0.16, 0.13), V(0.05, 0.16, 0.13)], gem: V(0, 0.84, 0.0), rs: 0.8 };
    }
    /* ---- head ---- */
    case 'circlet': {
      // A cloth band on a dark head block: a jewelled plate at the brow, two tails at the back.
      const b = G(it, 0, -0.06, 0);
      const head = M(new THREE.SphereGeometry(0.2, 12, 9), std('#2a2c33', { roughness: 0.8 }), b);
      head.scale.set(0.9, 1.15, 1);
      const neck = M(new THREE.CylinderGeometry(0.1, 0.16, 0.2, 10), std('#2a2c33', { roughness: 0.8 }), b);
      neck.position.y = -0.26;
      const band = G(b, 0, 0.07, 0);
      band.rotation.x = 0.18;
      const bm = std('#4a3b52', { roughness: 0.95, bumpScale: 0.03 });
      const cl = M(new THREE.CylinderGeometry(0.192, 0.192, 0.07, 18, 1, true), bm, band);
      cl.scale.x = 0.92;
      for (const y of [-0.035, 0.035]) ring(band, 0.194, 0.009, CLOTH_TRIM, y, 3, 22).scale.x = 0.92;
      const front = G(band, 0, 0.0, 0.195);
      const pk = new THREE.Shape();
      pk.moveTo(-0.08, -0.04);
      pk.lineTo(0.08, -0.04);
      pk.lineTo(0.05, 0.03);
      pk.lineTo(0, 0.1);
      pk.lineTo(-0.05, 0.03);
      pk.closePath();
      plate(front, pk, 0.015, metal('#9aa0a8', { roughness: 0.3 }));
      M(new THREE.OctahedronGeometry(0.03), std('#9fb4c8', { roughness: 0.2, flatShading: true }), front).position.set(0, 0.02, 0.02);
      for (const k of [-1, 1]) tube(band, [V(k * 0.04, 0, -0.18), V(k * 0.08, -0.12, -0.24), V(k * 0.07, -0.3, -0.22)], 0.018, CLOTH_B, 6, 4).scale.x = 1.4;
      return { at: band, band: { r: 0.2, y: 0, a0: 0.45, a1: 1.5 }, gem: V(0, 0.02, 0.22), rs: 0.6 };
    }
    case 'hood':
    case 'coif': {
      // A cowl with its face opening toward +Z, dark inside, falling into a short mantle.
      const mail = base === 'coif';
      const b = G(it, 0, -0.04, 0);
      const mat = mail ? metal('#8a9098', { roughness: 0.55, side: THREE.DoubleSide }) : leather('#5a3a24', { side: THREE.DoubleSide });
      const open = mail ? 0.62 : 0.8;
      const shell = M(new THREE.SphereGeometry(0.22, 14, 9, Math.PI / 2 + open, Math.PI * 2 - open * 2, 0, Math.PI * 0.8), mat, b);
      shell.scale.set(1, 1.15, 1.05);
      shell.position.y = 0.06;
      const inner = M(new THREE.SphereGeometry(0.2, 10, 7), std('#0e0b0a'), b);
      inner.position.set(0, 0.04, -0.01);
      inner.scale.set(0.95, 1.1, 0.95);
      const mantle = M(new THREE.CylinderGeometry(0.2, 0.36, 0.2, 14, 1, true), mat, b);
      mantle.position.y = -0.22;
      mantle.scale.z = 0.85;
      if (mail) {
        // Ring rows round the head and the mantle, a leather edge at the face and the hem.
        for (let i = 0; i < 6; i++) {
          const th = 0.35 + i * 0.25;
          const r = M(new THREE.TorusGeometry(0.222 * Math.sin(th), 0.007, 3, 20), DSTEEL, b);
          r.rotation.x = Math.PI / 2;
          r.position.y = 0.06 + 0.22 * Math.cos(th) * 1.15;
          r.scale.y = 1.05;
        }
        for (let i = 0; i < 3; i++) {
          const r = M(new THREE.TorusGeometry(0.21 + i * 0.05, 0.007, 3, 22), DSTEEL, b);
          r.rotation.x = Math.PI / 2;
          r.position.y = -0.15 - i * 0.05;
          r.scale.y = 0.85;
        }
        const edge = M(new THREE.TorusGeometry(0.36, 0.014, 3, 22), LEATHER_BELT, b);
        edge.rotation.x = Math.PI / 2;
        edge.position.y = -0.32;
        edge.scale.y = 0.85;
        return { at: b, rune: [V(-0.15, -0.22, 0.27), V(0.15, -0.22, 0.27)], gem: V(0, 0.29, 0.09), rs: 0.7 };
      }
      // The leather hood: a point falling back, a lacing at the throat.
      tube(b, [V(0, 0.28, -0.08), V(0, 0.3, -0.22), V(0, 0.18, -0.34)], 0.04, mat, 6, 5);
      M(new THREE.SphereGeometry(0.03, 5, 4), mat, b).position.set(0, 0.18, -0.34);
      M(new THREE.TorusGeometry(0.16, 0.015, 3, 16, Math.PI * 1.2), HIDE_D, b).position.set(0, 0.07, 0.12);
      (b.children[b.children.length - 1] as THREE.Mesh).rotation.z = -0.1 * Math.PI;
      M(new THREE.CylinderGeometry(0.025, 0.025, 0.05, 6), DSTEEL, b).position.set(0, -0.14, 0.22);
      return { at: b, rune: [V(-0.17, -0.24, 0.27), V(0.17, -0.24, 0.27)], gem: V(0, -0.14, 0.25), rs: 0.7 };
    }
    case 'helm': {
      const b = G(it, 0, -0.02, 0);
      const dome = M(new THREE.SphereGeometry(0.2, 14, 8, 0, Math.PI * 2, 0, Math.PI / 2), PLATE, b);
      dome.position.y = 0.08;
      dome.scale.set(1, 1.1, 1.08);
      const cheek = M(new THREE.CylinderGeometry(0.2, 0.19, 0.24, 14, 1, true), PLATE, b);
      cheek.position.y = -0.04;
      cheek.scale.z = 1.08;
      // Visor: a dark eye slit and breaths, a ridge on top, a rim at the neck.
      M(new THREE.BoxGeometry(0.26, 0.03, 0.06), std('#0e0b0a'), b).position.set(0, 0.04, 0.19);
      M(new THREE.BoxGeometry(0.03, 0.2, 0.05), PLATE, b).position.set(0, -0.02, 0.21);
      for (let i = 0; i < 3; i++) for (const k of [-1, 1]) M(new THREE.BoxGeometry(0.03, 0.012, 0.02), std('#0e0b0a'), b).position.set(k * 0.07, -0.05 - i * 0.03, 0.205);
      const crest = M(new THREE.TorusGeometry(0.2, 0.02, 4, 18, Math.PI), DSTEEL, b);
      crest.rotation.y = Math.PI / 2;
      crest.position.y = 0.08;
      crest.scale.y = 1.12;
      ring(b, 0.2, 0.016, DSTEEL, -0.16, 4, 22).scale.y = 1.08;
      ring(b, 0.205, 0.012, DSTEEL, 0.08, 4, 22).scale.y = 1.08;
      return { at: b, rune: [V(-0.14, 0.14, 0.17), V(0.14, 0.14, 0.17)], gem: V(0, 0.2, 0.17), rs: 0.7 };
    }
    /* ---- chest: a torso on its stand ---- */
    case 'robe': {
      const b = G(it, 0, 0.02, 0);
      torso(b, CLOTH_A);
      const skirt = M(new THREE.CylinderGeometry(0.2, 0.3, 0.4, 14, 1, true), cloth('#3d4558', { side: THREE.DoubleSide }), b);
      skirt.position.y = -0.48;
      skirt.scale.z = 0.75;
      const sash = M(new THREE.TorusGeometry(0.2, 0.025, 4, 18), CLOTH_TRIM, b);
      sash.rotation.x = Math.PI / 2;
      sash.scale.y = 0.68;
      sash.position.y = -0.18;
      M(new THREE.BoxGeometry(0.05, 0.36, 0.015), CLOTH_TRIM, b).position.set(0.06, -0.36, 0.17);
      // A wide collar and a V of trim down the front.
      const collar = M(new THREE.TorusGeometry(0.12, 0.035, 5, 16), CLOTH_B, b);
      collar.rotation.x = Math.PI / 2;
      collar.position.y = 0.26;
      for (const k of [-1, 1]) {
        const v = M(new THREE.BoxGeometry(0.035, 0.28, 0.015), CLOTH_B, b);
        v.position.set(k * 0.045, 0.1, 0.15);
        v.rotation.z = k * 0.3;
      }
      return { at: b, rune: [V(-0.12, -0.42, 0.24), V(0.12, -0.42, 0.24)], gem: V(0, 0.22, 0.15) };
    }
    case 'jerkin': {
      const b = G(it, 0, 0.0, 0);
      torso(b, HIDE, 1.03);
      // Laced front, stitched panels, a broad belt with a buckle, studded shoulders.
      M(new THREE.BoxGeometry(0.02, 0.44, 0.02), HIDE_D, b).position.set(0, 0.0, 0.155);
      for (let i = 0; i < 5; i++) {
        const l = M(new THREE.BoxGeometry(0.08, 0.008, 0.01), std('#d8d0bd'), b);
        l.position.set(0, -0.14 + i * 0.07, 0.165);
        l.rotation.z = i % 2 ? 0.4 : -0.4;
      }
      for (const k of [-1, 1]) M(new THREE.BoxGeometry(0.008, 0.4, 0.01), HIDE_D, b).position.set(k * 0.11, -0.02, 0.14);
      const belt = M(new THREE.TorusGeometry(0.205, 0.03, 4, 18), LEATHER_BELT, b);
      belt.rotation.x = Math.PI / 2;
      belt.scale.y = 0.68;
      belt.position.y = -0.22;
      M(new THREE.BoxGeometry(0.07, 0.06, 0.02), DSTEEL, b).position.set(0, -0.22, 0.15);
      for (const k of [-1, 1]) {
        const pa = M(new THREE.SphereGeometry(0.1, 8, 4, 0, Math.PI * 2, 0, Math.PI / 2), HIDE_D, b);
        pa.position.set(k * 0.2, 0.2, 0);
        pa.rotation.z = -k * 0.6;
        pa.scale.set(1, 0.6, 1.1);
      }
      return { at: b, rune: [V(-0.13, 0.13, 0.14), V(-0.05, 0.13, 0.155)], gem: V(0, -0.22, 0.165) };
    }
    case 'hauberk': {
      const b = G(it, 0, 0.03, 0);
      torso(b, MAIL, 1.04);
      mailRows(b, -0.27, 0.22, 9, 1.07, DSTEEL);
      const skirt = M(new THREE.CylinderGeometry(0.21, 0.26, 0.2, 14, 1, true), metal('#8a9098', { roughness: 0.55, side: THREE.DoubleSide }), b);
      skirt.position.y = -0.38;
      skirt.scale.z = 0.72;
      for (const y of [-0.33, -0.42]) {
        const r = M(new THREE.TorusGeometry(0.22 + (-0.33 - y) * 0.4, 0.008, 3, 20), DSTEEL, b);
        r.position.y = y;
        r.rotation.x = Math.PI / 2;
        r.scale.y = 0.72;
      }
      for (const k of [-1, 1]) {
        const sl = M(new THREE.CylinderGeometry(0.07, 0.085, 0.16, 8), MAIL, b);
        sl.position.set(k * 0.25, 0.16, 0);
        sl.rotation.z = k * 0.5;
      }
      const belt = M(new THREE.TorusGeometry(0.2, 0.022, 4, 18), LEATHER_BELT, b);
      belt.rotation.x = Math.PI / 2;
      belt.scale.y = 0.68;
      belt.position.y = -0.24;
      return { at: b, rune: [V(-0.08, 0.1, 0.165), V(0.08, 0.1, 0.165)], gem: V(0, -0.24, 0.15) };
    }
    case 'breastplate': {
      const b = G(it, 0, 0.0, 0);
      const pl = metal('#c2c8d0', { roughness: 0.22, flatShading: true });
      torso(b, pl);
      for (const k of [-1, 1]) {
        const pa = pauldron(b, k, pl, 1.4, 2, DSTEEL);
        pa.position.set(k * 0.23, 0.2, 0);
      }
      M(new THREE.BoxGeometry(0.02, 0.36, 0.03), pl, b).position.set(0, 0.02, 0.165);
      // The fauld: three lames below the waist.
      for (let i = 0; i < 3; i++) {
        const l = M(new THREE.CylinderGeometry(0.205 + i * 0.015, 0.215 + i * 0.015, 0.06, 14, 1, true), metal('#c2c8d0', { roughness: 0.22, side: THREE.DoubleSide }), b);
        l.position.y = -0.33 - i * 0.055;
        l.scale.z = 0.7;
      }
      ring(b, 0.2, 0.014, DSTEEL, -0.29, 4, 20).scale.y = 0.68;
      return { at: b, rune: [V(-0.1, 0.12, 0.155), V(0.1, 0.12, 0.155)], gem: V(0, -0.06, 0.16) };
    }
    /* ---- feet: a pair in profile ---- */
    case 'clothBoots':
    case 'leatherBoots':
    case 'mailBoots':
    case 'greaves': {
      const b = G(it, 0, -0.25, 0);
      b.rotation.y = -FACE + 0.5;
      const pair: THREE.Group[] = [];
      for (const z of [-0.1, 0.1]) {
        let bt: THREE.Group;
        if (base === 'clothBoots') {
          bt = boot(b, std('#4a4458', { roughness: 0.95, bumpScale: 0.03 }), { shaft: 0.3, cuff: CLOTH_TRIM, sole: HIDE_D });
          for (let i = 0; i < 3; i++) ring(bt, 0.086, 0.01, std('#2e2a36', { roughness: 1 }), 0.14 + i * 0.07, 3, 14).rotation.z = 0.15;
        } else if (base === 'leatherBoots') {
          bt = boot(b, HIDE, { shaft: 0.36, cuff: HIDE_D });
          ring(bt, 0.088, 0.014, LEATHER_BELT, 0.2, 3, 14);
          M(new THREE.BoxGeometry(0.035, 0.035, 0.03), DSTEEL, bt).position.set(0.085, 0.2, 0);
        } else if (base === 'mailBoots') {
          bt = boot(b, MAIL, { shaft: 0.36, toe: DSTEEL, sole: HIDE_D });
          for (let i = 0; i < 5; i++) ring(bt, 0.084 - i * 0.002, 0.006, DSTEEL, 0.12 + i * 0.065, 3, 14);
          ring(bt, 0.09, 0.014, LEATHER_BELT, 0.4, 3, 14);
        } else {
          bt = boot(b, PLATE, { shaft: 0.38, toe: PLATE, sole: DSTEEL });
          // Lames over the foot, a knee cop on top, a ridge down the shin.
          for (let i = 0; i < 3; i++) {
            const l = M(new THREE.SphereGeometry(0.09, 8, 3, 0, Math.PI * 2, 0, Math.PI / 2), PLATE, bt);
            l.position.set(0.05 + i * 0.05, 0.08 - i * 0.012, 0);
            l.scale.set(1.0, 0.55, 1.0);
          }
          const knee = M(new THREE.SphereGeometry(0.08, 8, 5), PLATE, bt);
          knee.position.set(0.04, 0.47, 0);
          knee.scale.set(0.8, 0.9, 1.1);
          M(new THREE.BoxGeometry(0.02, 0.32, 0.025), PLATE, bt).position.set(0.085, 0.26, 0);
          ring(bt, 0.088, 0.012, DSTEEL, 0.15, 3, 14);
        }
        bt.position.z = z;
        bt.position.x = z * 0.4;
        pair.push(bt);
      }
      // The near boot carries the marks, on its outer side.
      const near = G(pair[1]!, 0, 0, 0.09);
      return { at: near, rune: [V(0, 0.24, 0), V(0, 0.38, 0)], gem: V(-0.02, 0.12, 0.0), rs: 0.7 };
    }
    /* ---- amulets ---- */
    case 'pendant': {
      const b = G(it, 0, -0.1, 0);
      cord(b, DSTEEL);
      const set = G(b, 0, -0.12, 0);
      M(new THREE.TorusGeometry(0.09, 0.018, 5, 16), DSTEEL, set).scale.y = 1.35;
      const st = M(new THREE.SphereGeometry(0.08, 10, 8), std('#5a7f8f', { roughness: 0.15, metalness: 0.2 }), set);
      st.scale.set(1, 1.35, 0.55);
      const drop = M(new THREE.ConeGeometry(0.05, 0.08, 6), DSTEEL, set);
      drop.rotation.x = Math.PI;
      drop.position.y = -0.15;
      M(new THREE.TorusGeometry(0.025, 0.008, 4, 8), DSTEEL, set).position.y = 0.14;
      return { at: set, rune: [V(-0.06, -0.15, 0.03), V(0.06, -0.15, 0.03)], gem: V(0, 0, 0.05), rs: 0.6 };
    }
    case 'reliquary': {
      const b = G(it, 0, -0.1, 0);
      cord(b, DSTEEL);
      const box = G(b, 0, -0.14, 0);
      M(new THREE.BoxGeometry(0.2, 0.26, 0.08), metal('#6e747c', { roughness: 0.4 }), box);
      M(new THREE.BoxGeometry(0.12, 0.16, 0.02), std('#bfe0e8', { roughness: 0.05, transparent: true, opacity: 0.5 }), box).position.z = 0.04;
      M(new THREE.SphereGeometry(0.035, 6, 4), BONE_M, box).position.z = 0.025;
      const roof = M(new THREE.ConeGeometry(0.15, 0.1, 4), DSTEEL, box);
      roof.position.y = 0.18;
      roof.rotation.y = Math.PI / 4;
      roof.scale.z = 0.5;
      for (const k of [-1, 1]) M(new THREE.BoxGeometry(0.025, 0.28, 0.09), DSTEEL, box).position.x = k * 0.1;
      M(new THREE.BoxGeometry(0.2, 0.025, 0.09), DSTEEL, box).position.y = -0.13;
      return { at: box, rune: [V(-0.05, -0.1, 0.045), V(0.05, -0.1, 0.045)], gem: V(0, 0.18, 0.05), rs: 0.6 };
    }
    case 'torc': {
      // An open neck ring of twisted iron, heavy knobbed ends at the front.
      const b = G(it, 0, 0, 0);
      b.rotation.x = 0.9;
      const tm = metal('#7c838c', { roughness: 0.35 });
      const pts: THREE.Vector3[] = [];
      for (let i = 0; i <= 24; i++) {
        const ang = Math.PI / 2 + 0.45 + (i / 24) * (Math.PI * 2 - 0.9);
        pts.push(V(Math.cos(ang) * 0.24, 0, Math.sin(ang) * 0.24));
      }
      tube(b, pts, 0.034, tm, 36, 6);
      const tw = std('#3c4048', { roughness: 0.5, metalness: 0.6 });
      for (let i = 2; i < 23; i += 2) {
        const r = M(new THREE.TorusGeometry(0.034, 0.008, 3, 8), tw, b);
        r.position.copy(pts[i]!);
        r.quaternion.setFromUnitVectors(V(0, 0, 1), pts[i + 1]!.clone().sub(pts[i]!).normalize());
      }
      for (const p of [pts[0]!, pts[24]!]) {
        M(new THREE.SphereGeometry(0.06, 8, 6), tm, b).position.copy(p);
      }
      return { at: b, rune: [V(-0.1, 0.035, -0.21), V(0.1, 0.035, -0.21)], gem: pts[0]!.clone().lerp(pts[24]!, 0.5).setY(0.02), rs: 0.6 };
    }
    case 'eye': {
      const b = G(it, 0, -0.1, 0);
      cord(b, DSTEEL);
      const set = G(b, 0, -0.12, 0);
      // An almond of dark iron round a pale eye and its iris.
      const al = new THREE.Shape();
      al.moveTo(-0.19, 0);
      al.quadraticCurveTo(0, 0.17, 0.19, 0);
      al.quadraticCurveTo(0, -0.17, -0.19, 0);
      plate(set, al, 0.03, DSTEEL);
      const white = M(new THREE.SphereGeometry(0.1, 12, 8), std('#e6e2da', { roughness: 0.3 }), set);
      white.scale.set(1.4, 0.68, 0.35);
      white.position.z = 0.02;
      const iris = M(new THREE.SphereGeometry(0.05, 10, 8), emis('#6aa0b8', '#2a6a8a', 0.4), set);
      iris.position.z = 0.045;
      iris.scale.z = 0.5;
      M(new THREE.SphereGeometry(0.022, 6, 5), std('#0c0a0c'), set).position.z = 0.068;
      for (const k of [-1, 1]) {
        const lash = M(new THREE.BoxGeometry(0.012, 0.05, 0.012), DSTEEL, set);
        lash.position.set(k * 0.08, -0.1, 0);
        lash.rotation.z = k * 0.4;
      }
      M(new THREE.BoxGeometry(0.012, 0.06, 0.012), DSTEEL, set).position.y = -0.12;
      return { at: set, rune: [V(-0.1, 0.1, 0.02), V(0.1, 0.1, 0.02)], gem: V(0, -0.17, 0.02), rs: 0.6 };
    }
    case 'talisman': {
      // A disc of carved bone on a leather thong, a ward knot cut in it, feathers and beads beneath.
      const b = G(it, 0, -0.1, 0);
      cord(b, LEATHER_BELT);
      const set = G(b, 0, -0.14, 0);
      const disc = M(new THREE.CylinderGeometry(0.13, 0.13, 0.03, 12), BONE_M, set);
      disc.rotation.x = Math.PI / 2;
      ring(set, 0.13, 0.012, LEATHER_BELT, 0, 3, 16).rotation.x = 0;
      const knot = M(new THREE.TorusKnotGeometry(0.055, 0.009, 36, 4, 2, 3), std('#3a2a24'), set);
      knot.position.z = 0.017;
      knot.scale.z = 0.3;
      const fe = std('#5a4a3e', { roughness: 1, side: THREE.DoubleSide });
      for (const k of [-1, 0, 1]) {
        limb(set, V(k * 0.05, -0.12, 0), V(k * 0.07, -0.2, 0), 0.004, 0.004, LEATHER_BELT, 3);
        M(new THREE.SphereGeometry(0.018, 5, 4), k === 0 ? std('#7a2e2a') : BONE_M, set).position.set(k * 0.07, -0.2, 0);
        const f = M(new THREE.SphereGeometry(0.03, 5, 3), fe, set);
        f.scale.set(0.45, 1.5, 0.15);
        f.position.set(k * 0.075, -0.27, 0);
      }
      return { at: set, rune: [V(-0.07, 0.07, 0.02), V(0.07, 0.07, 0.02)], gem: V(0, 0, 0.03), rs: 0.6 };
    }
    /* ---- rings: big, upright, their setting on top ---- */
    case 'signet': {
      const b = G(it, 0, -0.05, 0);
      b.rotation.x = -0.35;
      const sm = metal('#a0a6ae', { roughness: 0.3 });
      hoop(b, 0.17, 0.035, sm, 1.3);
      const face = M(new THREE.CylinderGeometry(0.1, 0.08, 0.05, 12), sm, b);
      face.position.y = 0.2;
      face.scale.z = 0.8;
      // The engraved seal: a dark field and a raised tower mark.
      const fld = M(new THREE.CylinderGeometry(0.08, 0.08, 0.01, 12), DSTEEL, b);
      fld.position.y = 0.226;
      fld.scale.z = 0.8;
      M(new THREE.BoxGeometry(0.05, 0.012, 0.07), sm, b).position.y = 0.232;
      M(new THREE.BoxGeometry(0.1, 0.012, 0.025), sm, b).position.set(0, 0.232, 0.03);
      return { at: b, arc: { r: 0.17, z: 0.05, a0: -2.5, a1: -0.64 }, gem: V(0, 0.24, 0.0), rs: 0.6 };
    }
    case 'band': {
      const b = G(it, 0, 0, 0);
      b.rotation.x = -0.35;
      const sm = metal('#a0a6ae', { roughness: 0.25 });
      const r = M(new THREE.CylinderGeometry(0.19, 0.19, 0.12, 24, 1, true), metal('#a0a6ae', { roughness: 0.25, side: THREE.DoubleSide }), b);
      r.rotation.x = Math.PI / 2;
      for (const z of [-0.06, 0.06]) M(new THREE.TorusGeometry(0.19, 0.014, 4, 24), sm, b).position.z = z;
      M(new THREE.TorusGeometry(0.192, 0.006, 3, 24), DSTEEL, b);
      return { at: b, arc: { r: 0.19, z: 0.075, a0: -2.5, a1: -0.64 }, gem: V(0, 0.2, 0.06), rs: 0.6 };
    }
    case 'stoneRing': {
      const b = G(it, 0, -0.03, 0);
      b.rotation.x = -0.35;
      hoop(b, 0.17, 0.05, stone('#6e6a66'), 1.2);
      const top = M(new THREE.DodecahedronGeometry(0.09), stone('#5f6a64'), b);
      top.position.y = 0.22;
      top.scale.set(1.2, 0.8, 1);
      return { at: b, arc: { r: 0.17, z: 0.065, a0: -2.5, a1: -0.64 }, gem: V(0, 0.29, 0.0), rs: 0.6 };
    }
    case 'eyeRing': {
      const b = G(it, 0, -0.05, 0);
      b.rotation.x = -0.35;
      const sm = metal('#7c838c', { roughness: 0.35 });
      hoop(b, 0.17, 0.03, sm, 1.3);
      const set = G(b, 0, 0.21, 0);
      set.rotation.x = -Math.PI / 2 + 0.35 + 0.6;
      const al = new THREE.Shape();
      al.moveTo(-0.12, 0);
      al.quadraticCurveTo(0, 0.11, 0.12, 0);
      al.quadraticCurveTo(0, -0.11, -0.12, 0);
      plate(set, al, 0.03, DSTEEL);
      const white = M(new THREE.SphereGeometry(0.065, 10, 6), std('#e6e2da', { roughness: 0.3 }), set);
      white.scale.set(1.4, 0.68, 0.35);
      white.position.z = 0.02;
      const iris = M(new THREE.SphereGeometry(0.032, 8, 6), emis('#6aa0b8', '#2a6a8a', 0.4), set);
      iris.position.z = 0.035;
      iris.scale.z = 0.5;
      M(new THREE.SphereGeometry(0.014, 6, 4), std('#0c0a0c'), set).position.z = 0.05;
      return { at: b, arc: { r: 0.17, z: 0.045, a0: -2.5, a1: -0.64 }, gem: V(-0.17, 0, 0.045), rs: 0.6 };
    }
    case 'wardRing': {
      // A thick band ringed by a guard of studs, three small stones set round the top.
      const b = G(it, 0, -0.03, 0);
      b.rotation.x = -0.35;
      const sm = metal('#8a9098', { roughness: 0.35 });
      hoop(b, 0.17, 0.04, sm, 1.4);
      for (let i = 0; i < 14; i++) {
        const ang = (i / 14) * Math.PI * 2;
        M(new THREE.OctahedronGeometry(0.022), DSTEEL, b).position.set(Math.cos(ang) * 0.215, Math.sin(ang) * 0.215, 0);
      }
      for (const k of [-1, 0, 1]) {
        const st = M(new THREE.SphereGeometry(k === 0 ? 0.045 : 0.032, 8, 6), std('#4f7a6a', { roughness: 0.15, metalness: 0.2 }), b);
        st.position.set(Math.sin(k * 0.4) * 0.2, Math.cos(k * 0.4) * 0.2 + 0.02, 0.04);
      }
      return { at: b, arc: { r: 0.17, z: 0.06, a0: -2.5, a1: -0.64 }, gem: V(0, 0.27, 0.0), rs: 0.6 };
    }
  }
};

/** Slot groups for the framing and the tilt. */
const isWeapon = (base: GearBaseId): boolean => GEAR_BASES[base].slot === 'weapon';
const TILTED: ReadonlySet<GearBaseId> = new Set(['longsword', 'handaxe', 'mace', 'warhammer', 'wand', 'sickle', 'greatsword', 'greataxe', 'maul', 'staff', 'quarterstaff', 'offAxe']);

/** A piece's rarity on it: runes in its colour (uncommon up) and, from rare up, a gem with its glow. */
const decorate = (piece: Piece, rarity: GearRarity): void => {
  const nr = RUNES[rarity];
  if (nr > 0 && (piece.rune || piece.arc || piece.band)) {
    const s = piece.rs ?? 1;
    const rm = emis('#181818', RUNE[rarity], 1.25);
    for (let i = 0; i < nr; i++) {
      const t = nr === 1 ? 0.5 : i / (nr - 1);
      const rn = M(new THREE.BoxGeometry(0.028 * s, 0.056 * s, 0.014), rm, piece.at);
      if (piece.arc) {
        const { r, z, a0, a1 } = piece.arc;
        const ang = a0 + (a1 - a0) * t;
        rn.position.set(Math.cos(ang) * r, Math.sin(ang) * r, z);
        rn.rotation.z = ang + Math.PI / 2;
      } else if (piece.band) {
        const { r, y, a0, a1 } = piece.band;
        const ang = a0 + (a1 - a0) * t;
        rn.position.set(Math.sin(ang) * r, y, Math.cos(ang) * r);
        rn.rotation.y = ang;
      } else if (piece.rune) rn.position.copy(piece.rune[0].clone().lerp(piece.rune[1], t));
    }
  }
  const gr = GEM[rarity];
  if (gr > 0 && piece.gem) {
    const r = 0.036 * gr * (piece.rs ?? 1);
    M(new THREE.OctahedronGeometry(r), emis(RUNE_LIGHT[rarity], RUNE[rarity], 0.8, { flatShading: true }), piece.at).position.copy(piece.gem);
    glowSprite(piece.at, RUNE[rarity], r * 8, piece.gem.clone().setZ(piece.gem.z + 0.02), 0.6);
  }
};

/**
 * One of the 44 gear bases at a rarity, for its card (and later the hero's hand). The piece alone on its
 * axis, like the unique pieces: common is bare; uncommon to very rare carry more runes in their rarity's colour
 * and, from rare up, a gem; a unique piece burns in dragon red with a faint aura. Brightness only from
 * emissive and glow sprites, never lights. (`gear`, not `base`: `base` is the display stand in BaseOpts.)
 */
export const gearItem = (o: BaseOpts & { gear: GearBaseId; rarity: GearRarity }): Model => {
  const c = begin(o, 0.55, 400 + o.gear.length * 7);
  const { g } = c;
  const def = GEAR_BASES[o.gear];
  const it = G(g, 0, 0.5, 0);
  const piece = build(o.gear, it);
  decorate(piece, o.rarity);
  // Turn the face to the card; long weapons lie on the diagonal.
  it.rotation.set(0, FACE, TILTED.has(o.gear) ? -0.7 : 0);
  if (def.slot === 'ring') it.scale.setScalar(1.6);
  if (def.slot === 'amulet') it.scale.setScalar(1.5);
  if (o.rarity === 'unique') glowSprite(g, RUNE.unique, 1.1, V(0, 0.5, 0), 0.16);
  const height = isWeapon(o.gear) ? (def.hands === 2 ? 1.3 : 1.0) : 0.9;
  return done(c, height);
};

/** Where a held piece is gripped, as a share of its height from the bottom (the rest: near the bottom). */
const GRIP: Partial<Record<GearBaseId, number>> = {
  staff: 0.36, quarterstaff: 0.36, maul: 0.22, greataxe: 0.2, greatsword: 0.14, longbow: 0.5, shortbow: 0.5, heavyCrossbow: 0.45,
  shield: 0.5, grimoire: 0.5, orb: 0.3, holySymbol: 0.25, totem: 0.25, toolkit: 0.6, quiver: 0.5, offAxe: 0.12, sickle: 0.12,
};
/** How big a held piece is next to a hero (the cards draw them larger); slim pieces a little bigger, or they vanish at the table's distance. */
export const HELD_SCALE = 0.8;
const HELD_SCALE_BY: Partial<Record<GearBaseId, number>> = { wand: 1.15, sickle: 1.25, shortbow: 1.05, longbow: 0.95, orb: 0.72, totem: 0.78, grimoire: 0.85, holySymbol: 0.9 };

/**
 * A piece for a hero's hand (docs/EQUIPO.md §10): the same model as its card, with its rarity, its grip at the
 * origin and pointing up (+Y), its face to +Z (the hero's front). The hero's builders hang it on a hand pivot.
 */
export const gearHeld = (base: GearBaseId, rarity: GearRarity): THREE.Group => {
  const wrap = new THREE.Group();
  const it = G(wrap, 0, 0, 0);
  decorate(build(base, it), rarity);
  const box = new THREE.Box3().setFromObject(it);
  const h = box.max.y - box.min.y;
  it.position.y = -(box.min.y + h * (GRIP[base] ?? 0.1));
  wrap.scale.setScalar(HELD_SCALE_BY[base] ?? HELD_SCALE);
  return wrap;
};
